import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { synthwave84 } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { NavigationConfirmationModal, StanButton, StanInput } from '@/components';
import { useDirtyState } from '@/hooks/useDirtyState';
import { useStanRouter } from '@/hooks/useStanRouter';
import { useNavigationStore } from '@/stores/navigationStore';
import { toast } from 'react-toastify';
import { FiExternalLink, FiSave, FiX } from 'react-icons/fi';

interface FormData {
    title: string;
    description: string;
    email: string;
}

export default function InternalsNavigationConfirmationPage() {
    const [activePlayground, setActivePlayground] = useState('preview');
    const [activeBasic, setActiveBasic] = useState('preview');
    const { push } = useStanRouter();
    const { showConfirmation, cancelNavigation, proceedWithNavigation } = useNavigationStore();

    const playgroundForm = useForm<FormData>({
        defaultValues: {
            title: '',
            description: '',
            email: '',
        },
    });

    useDirtyState(playgroundForm.formState.isDirty, 'playground-form');

    const handlePlaygroundSave = (data: FormData) => {
        toast.success('Form saved successfully!');
        playgroundForm.reset(data);
    };

    const handleNavigateWithSkip = () => {
        push('/internals', { skipDirtyCheck: true });
    };

    const handleNavigateNormal = () => {
        push('/internals');
    };

    const CodeBlock = ({ code }: { code: string }) => (
        <div className='border-muted overflow-hidden rounded-md border'>
            <div className='text-body-md [&_code]:w-full! [&_code]:!p-0 [&_code]:!text-base [&_code]:!leading-relaxed [&_pre]:!m-0 [&_pre]:!p-6'>
                <SyntaxHighlighter language='typescript' style={synthwave84} showLineNumbers={false}>
                    {code.trim()}
                </SyntaxHighlighter>
            </div>
        </div>
    );

    return (
        <div className='mx-auto max-w-6xl space-y-12 p-6 pb-40'>
            {/* Page Header */}
            <div className='space-y-2'>
                <h1 className='text-heading-h1 text-dark'>Navigation Confirmation</h1>
                <p className='text-body-lg text-gray'>Prevent users from losing unsaved changes when navigating away</p>
            </div>

            {/* Interactive Playground */}
            <section className='space-y-6'>
                <div>
                    <h2 className='text-heading-h2 text-dark'>Interactive Playground</h2>
                    <p className='text-body-md text-gray mt-2'>
                        Edit the form and try navigating away to see the confirmation modal
                    </p>
                </div>

                <div className='border-muted rounded-lg border'>
                    <div className='border-muted flex gap-6 border-b px-6 pt-2'>
                        <button
                            onClick={() => setActivePlayground('preview')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activePlayground === 'preview'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Preview
                        </button>
                        <button
                            onClick={() => setActivePlayground('code')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activePlayground === 'code'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Code
                        </button>
                    </div>

                    <div className='p-6'>
                        {activePlayground === 'preview' ? (
                            <div className='grid grid-cols-1 gap-8 lg:grid-cols-2'>
                                {/* Form Section */}
                                <div className='space-y-6'>
                                    <h3 className='text-heading-h3 text-dark'>Sample Form</h3>

                                    <form
                                        onSubmit={playgroundForm.handleSubmit(handlePlaygroundSave)}
                                        className='space-y-6'
                                    >
                                        <Controller
                                            name='title'
                                            control={playgroundForm.control}
                                            render={({ field }) => (
                                                <StanInput {...field} topLabel='Title' placeholder='Enter a title' />
                                            )}
                                        />

                                        <Controller
                                            name='description'
                                            control={playgroundForm.control}
                                            render={({ field }) => (
                                                <StanInput
                                                    {...field}
                                                    topLabel='Description'
                                                    placeholder='Enter a description'
                                                />
                                            )}
                                        />

                                        <Controller
                                            name='email'
                                            control={playgroundForm.control}
                                            render={({ field }) => (
                                                <StanInput
                                                    {...field}
                                                    type='email'
                                                    topLabel='Email'
                                                    placeholder='Enter an email'
                                                />
                                            )}
                                        />

                                        <div className='flex gap-3'>
                                            <StanButton
                                                name='internals-save-changes-button'
                                                type='submit'
                                                icon={FiSave}
                                                className='flex-1'
                                            >
                                                Save Changes
                                            </StanButton>
                                            <StanButton
                                                name='internals-reset-form-button'
                                                type='button'
                                                variant='outline'
                                                icon={FiX}
                                                onClick={() => playgroundForm.reset()}
                                                className='flex-1'
                                            >
                                                Reset Form
                                            </StanButton>
                                        </div>
                                    </form>

                                    <div className='border-muted rounded-md border p-4'>
                                        <div className='flex items-center justify-between'>
                                            <span className='text-label-sm-regular text-gray'>
                                                Has unsaved changes:
                                            </span>
                                            <span
                                                className={`text-label-sm-medium ${
                                                    playgroundForm.formState.isDirty ? 'text-warning' : 'text-success'
                                                }`}
                                            >
                                                {playgroundForm.formState.isDirty ? 'Yes' : 'No'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Navigation Test Section */}
                                <div className='space-y-6'>
                                    <h3 className='text-heading-h3 text-dark'>Test Navigation</h3>

                                    <div className='border-muted bg-bg-subtle border-l-4 p-4'>
                                        <p className='text-label-sm-medium text-dark mb-2'>How to Test:</p>
                                        <ol className='text-label-sm-regular text-gray ml-4 list-decimal space-y-1'>
                                            <li>Edit any field in the form</li>
                                            <li>Click a navigation button below</li>
                                            <li>Observe the confirmation modal</li>
                                        </ol>
                                    </div>

                                    <div className='space-y-3'>
                                        <StanButton
                                            name='internals-navigate-normal-button'
                                            variant='outline'
                                            icon={FiExternalLink}
                                            onClick={handleNavigateNormal}
                                            className='w-full'
                                        >
                                            Navigate (Normal)
                                        </StanButton>

                                        <StanButton
                                            name='internals-navigate-skip-button'
                                            variant='outline'
                                            icon={FiExternalLink}
                                            onClick={handleNavigateWithSkip}
                                            className='w-full'
                                        >
                                            Navigate (Skip Check)
                                        </StanButton>

                                        <StanButton
                                            name='internals-navigate-dashboard-button'
                                            variant='outline'
                                            icon={FiExternalLink}
                                            onClick={() => push('/dashboard')}
                                            className='w-full'
                                        >
                                            Navigate to Dashboard
                                        </StanButton>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <CodeBlock
                                code={`import { useForm } from 'react-hook-form';
import { useDirtyState } from '@/hooks/useDirtyState';
import { useStanRouter } from '@/hooks/useStanRouter';

export const MyForm = () => {
  const { push } = useStanRouter();
  const form = useForm();
  
  // Register dirty state
  useDirtyState(form.formState.isDirty, 'my-form');
  
  const handleSave = (data) => {
    // Save logic...
    form.reset(data); // Clear dirty state
  };
  
  return (
    <form onSubmit={form.handleSubmit(handleSave)}>
      {/* Form fields */}
      <button type="submit">Save</button>
      <button onClick={() => push('/other-page')}>
        Navigate
      </button>
    </form>
  );
};`}
                            />
                        )}
                    </div>
                </div>
            </section>

            {/* Basic Usage */}
            <section className='space-y-6'>
                <div>
                    <h2 className='text-heading-h2 text-dark'>Basic Usage</h2>
                    <p className='text-body-md text-gray mt-2'>
                        Three simple steps to implement navigation confirmation
                    </p>
                </div>

                <div className='border-muted rounded-lg border'>
                    <div className='border-muted flex gap-6 border-b px-6 pt-2'>
                        <button
                            onClick={() => setActiveBasic('preview')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activeBasic === 'preview'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Preview
                        </button>
                        <button
                            onClick={() => setActiveBasic('code')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activeBasic === 'code'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Code
                        </button>
                    </div>

                    <div className='p-6'>
                        {activeBasic === 'preview' ? (
                            <div className='space-y-8'>
                                {/* Step 1 */}
                                <div className='border-muted space-y-4 rounded-md border p-6'>
                                    <h3 className='text-heading-h3 text-dark'>Step 1: Register Dirty State</h3>
                                    <p className='text-body-md text-gray'>
                                        Use the{' '}
                                        <span className='bg-neutral-subtle rounded px-2 py-1'>useDirtyState</span> hook
                                        to register your component&apos;s dirty state
                                    </p>
                                    <div className='border-muted bg-bg-subtle border-l-4 p-4'>
                                        <p className='text-label-sm-regular text-gray'>
                                            The hook automatically registers/unregisters based on the isDirty value and
                                            cleans up on component unmount
                                        </p>
                                    </div>
                                </div>

                                {/* Step 2 */}
                                <div className='border-muted space-y-4 rounded-md border p-6'>
                                    <h3 className='text-heading-h3 text-dark'>Step 2: Navigate with useStanRouter</h3>
                                    <p className='text-body-md text-gray'>
                                        Use <span className='bg-neutral-subtle rounded px-2 py-1'>useStanRouter</span>{' '}
                                        for navigation - it automatically checks for dirty state
                                    </p>
                                    <div className='border-muted bg-bg-subtle border-l-4 p-4'>
                                        <p className='text-label-sm-regular text-gray'>
                                            If any component is dirty, a confirmation modal appears before navigation
                                        </p>
                                    </div>
                                </div>

                                {/* Step 3 */}
                                <div className='border-muted space-y-4 rounded-md border p-6'>
                                    <h3 className='text-heading-h3 text-dark'>Step 3: Skip Check When Needed</h3>
                                    <p className='text-body-md text-gray'>
                                        After successful saves, use{' '}
                                        <span className='bg-neutral-subtle rounded px-2 py-1'>
                                            skipDirtyCheck: true
                                        </span>
                                    </p>
                                    <div className='border-muted bg-bg-subtle border-l-4 p-4'>
                                        <p className='text-label-sm-regular text-gray'>
                                            Use this after form submission, delete operations, or any programmatic
                                            navigation where data is already saved
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <CodeBlock
                                code={`// Step 1: Register dirty state
import { useDirtyState } from '@/hooks/useDirtyState';

const form = useForm();
useDirtyState(form.formState.isDirty, 'my-form-id');

// Step 2: Navigate with dirty check
import { useStanRouter } from '@/hooks/useStanRouter';

const { push } = useStanRouter();
push('/some-route'); // Automatically checks dirty state

// Step 3: Skip dirty check after save
const handleSave = async (data) => {
  await saveData(data);
  push('/success-page', { skipDirtyCheck: true });
};`}
                            />
                        )}
                    </div>
                </div>
            </section>

            {/* API Reference */}
            <section className='space-y-6'>
                <div>
                    <h2 className='text-heading-h2 text-dark'>API Reference</h2>
                </div>

                <div className='space-y-4'>
                    <div className='border-muted rounded-lg border p-6'>
                        <h3 className='text-heading-h3 text-dark mb-4'>useDirtyState</h3>
                        <div className='space-y-3'>
                            <div className='flex items-start gap-4'>
                                <span className='bg-brand-dimmed text-brand-main border-brand-subtle shrink-0 rounded-md border px-2 py-1 font-mono text-sm font-medium'>
                                    isDirty
                                </span>
                                <span className='text-body-md text-gray pt-1'>
                                    Boolean indicating if component has unsaved changes
                                </span>
                            </div>
                            <div className='flex items-start gap-4'>
                                <span className='bg-brand-dimmed text-brand-main border-brand-subtle shrink-0 rounded-md border px-2 py-1 font-mono text-sm font-medium'>
                                    componentId
                                </span>
                                <span className='text-body-md text-gray pt-1'>
                                    Optional unique identifier for the component
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className='border-muted rounded-lg border p-6'>
                        <h3 className='text-heading-h3 text-dark mb-4'>useStanRouter</h3>
                        <div className='space-y-3'>
                            <div className='flex items-start gap-4'>
                                <span className='bg-brand-dimmed text-brand-main border-brand-subtle shrink-0 rounded-md border px-2 py-1 font-mono text-sm font-medium'>
                                    push(path, options)
                                </span>
                                <span className='text-body-md text-gray pt-1'>
                                    Navigate to path with optional skipDirtyCheck
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Global Navigation Confirmation Modal */}
            <NavigationConfirmationModal
                isOpen={showConfirmation}
                onStay={cancelNavigation}
                onDiscard={proceedWithNavigation}
            />
        </div>
    );
}
